import { z } from "zod";

import {
  COLORS,
  DETAILS_POPUP_TYPES,
  IMAGE_MIMES,
  SIZE_ELEMENT_TYPES,
  SIZE_NUMBER_ELEMENT_TYPES,
} from "@shared/constants/ui";

import { type IconNameType, iconKeys } from "../../ui/icon";
import { createComponentSchemas } from "../utils";
import { StrapiMediaSchema } from "./media.schema";

export const DetailsPopupTypesSchema = z.enum(DETAILS_POPUP_TYPES);

export const DetailsPopupInfoSchema = z.object({
  type: DetailsPopupTypesSchema,
  id: z.number().int().positive(),
  title: z.string(),
  detailList: z.array(
    z.object({
      id: z.number().int().positive(),
      text: z.string(),
    }),
  ),
});

// Цвета
export const ColorSchema = z.enum(COLORS);
export type ColorType = z.infer<typeof ColorSchema>;

// Размеры (строковые)
export const SizeElementSchema = z.enum(SIZE_ELEMENT_TYPES);
export type SizeElementType = z.infer<typeof SizeElementSchema>;

// Типы изображений
export const ImageMimesSchema = z.union(IMAGE_MIMES.map((n) => z.literal(n)));
export type ImageMimesType = z.infer<typeof ImageMimesSchema>;

// Размеры (числовые)
export const SizeNumberElementSchema = z.union(
  SIZE_NUMBER_ELEMENT_TYPES.map((n) => z.literal(n)),
);
export type SizeNumberElementType = z.infer<typeof SizeNumberElementSchema>;

// Иконки
export const IconsSchema = z.enum(
  iconKeys as [IconNameType, ...IconNameType[]],
);

export const IconBaseSchema = z.object({
  id: z.number().int().positive().optional(),
  name: IconsSchema,
});

export const CodeIconSchema = z.object({
  id: z.number().int().positive(),
  documentId: z.string(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
  publishedAt: z.string().datetime().optional(),
  name: z.string(),
  svg_code: z.string(),
  svg_file: StrapiMediaSchema,
});

export const ColorIconSchema = z.object({
  id: z.number().int().positive(),
  icon: IconBaseSchema,
  fillColor: ColorSchema,
  strokeColor: ColorSchema,
  uicon: CodeIconSchema.optional().nullable(),
});

export const RegularIconSchema = z.object({
  id: z.number().int().positive(),
  icon: IconBaseSchema,
  fillColor: ColorSchema,
  strokeColor: ColorSchema.optional(),
  uicon: CodeIconSchema.optional().nullable(),
});

export const SocialLinkSchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  url: z.string(),
});

// Тип для иконок транспорта (виды транспорта в городе)
export type TransportIcon = z.infer<typeof IconBaseSchema>;

// Тип для названий иконок транспорта (связан с IconNameType)
export type TransportIconName = IconNameType;

export type CodeIconType = z.infer<typeof CodeIconSchema>;

// Схема для названий транспорта (массив строк с валидацией через IconNameType)
export const TransportNamesSchema = z.array(IconsSchema);

// Файл
export const FileSchema = z.object({
  id: z.number().int().positive(),
  title: z.string().min(1),
  file: StrapiMediaSchema,
});

export const ButtonBaseSchema = z.object({
  text: z.string().min(1),
  link: z.string().min(1),
  pageIcon: ColorIconSchema.optional(),
});

export const Button = createComponentSchemas("ui.button", ButtonBaseSchema);

const ButtonBaseComponentSchema = z.object({
  text: z.string().min(1),
  link: z.string().min(1),
});

export const ButtonBaseComponent = createComponentSchemas(
  "ui.button-base",
  ButtonBaseComponentSchema,
);

const InfoListItemBaseSchema = z.object({
  title: z.string().min(1),
  value: z.string().min(1),
  icon: IconBaseSchema.nullable().optional(),
  iconColor: z
    .enum(["gold", "cadmium", "violet", "azure", "umbra"])
    .optional()
    .default("violet"),
});

export const InfoListItem = createComponentSchemas(
  "ui.info-list-item",
  InfoListItemBaseSchema,
);

const CityIconsBaseSchema = z.object({
  cityName: z.string().min(1),
  icons: IconBaseSchema.array().optional(),
});

export const CityIcons = createComponentSchemas(
  "sections.city-icons",
  CityIconsBaseSchema,
);

const WorkTimeListBaseSchema = z.object({
  timeSlot: z.string(),
});

export const WorkTimeList = createComponentSchemas(
  "ui.work-time-list",
  WorkTimeListBaseSchema,
);

// Коды городов для карты
export type CityCode = "mineral" | "pyat" | "nal" | "vlad";

// Маппинг кодов городов на ID путей в SVG
export const CITY_PATH_IDS: Record<CityCode, string> = {
  vlad: "vlad",
  pyat: "pyati",
  mineral: "mineral",
  nal: "nalchik",
};

// Базовый тип для города с транспортом
export type CityWithTransportBase = {
  name: CityCode;
  title?: string;
  cityName?: string;
};

// Тип для города с транспортом в виде строк (для map-block)
// transport содержит ключи из TRANSPORT_NAMES, а не иконки напрямую
export type CityWithTransportStrings = CityWithTransportBase & {
  transport: string[];
};

// Тип для города с транспортом в виде объектов (для line-map)
export type CityWithTransportIcons = CityWithTransportBase & {
  icons?: TransportIcon[];
};

export type ButtonBase = z.infer<typeof Button.base>;
export type ButtonDynamic = z.infer<typeof Button.dynamic>;

export type ButtonBaseComponentBase = z.infer<typeof ButtonBaseComponent.base>;
export type ButtonBaseComponentDynamic = z.infer<
  typeof ButtonBaseComponent.dynamic
>;

export type CityIconsBase = z.infer<typeof CityIcons.base>;
export type CityIconsDynamic = z.infer<typeof CityIcons.dynamic>;

export type InfoListItemBase = z.infer<typeof InfoListItem.base>;
export type InfoListItemDynamic = z.infer<typeof InfoListItem.dynamic>;

export type WorkTimeListBase = z.infer<typeof WorkTimeList.base>;
export type WorkTimeListDynamic = z.infer<typeof WorkTimeList.dynamic>;
